import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { authedUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/**
 * Searchable single-select combobox for employees.
 * Handles orgs with 1000+ employees by using a virtualised-friendly Command
 * list with typeahead filter and initials-matching.
 */
export default function EmployeeCombobox({
  employees = [],
  value,
  onChange,
  placeholder = "Assign to…",
  disabled = false,
  allowClear = true,
  testid = "employee-combobox",
}) {
  const [open, setOpen] = useState(false);
  const selected = employees.find((e) => e.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testid}
          disabled={disabled}
          className="w-full justify-between font-normal h-10"
        >
          <span className="flex items-center gap-2 truncate">
            {selected ? (
              <>
                <Avatar className="h-5 w-5 shrink-0">
                  <AvatarImage src={authedUrl(selected.avatar_url)} />
                  <AvatarFallback className="text-[9px]">
                    {(selected.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{selected.full_name}</span>
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <div className="flex items-center gap-1">
            {selected && allowClear && (
              <span
                role="button"
                tabIndex={0}
                data-testid={`${testid}-clear`}
                onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange?.(""); } }}
                className="rounded p-0.5 opacity-60 hover:opacity-100 hover:bg-accent inline-flex"
              >
                <X className="w-3 h-3" />
              </span>
            )}
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue is the value we set on CommandItem (id). Do our own
            // filter across full_name, email, initials, and department.
            const e = employees.find((x) => x.id === itemValue);
            if (!e) return 0;
            const q = search.toLowerCase().trim();
            if (!q) return 1;
            const initials = (e.full_name || "").split(/\s+/).map((s) => s[0] || "").join("").toLowerCase();
            const hay = `${e.full_name || ""} ${e.email || ""} ${e.department || ""} ${e.designation || ""} ${initials}`.toLowerCase();
            return hay.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search employees, initials, department…" data-testid={`${testid}-input`} />
          <CommandList>
            <CommandEmpty>No employee found.</CommandEmpty>
            <CommandGroup>
              {employees.map((e) => (
                <CommandItem
                  key={e.id}
                  value={e.id}
                  data-testid={`${testid}-opt-${e.id}`}
                  onSelect={(v) => { onChange?.(v === value ? "" : v); setOpen(false); }}
                  className="gap-2"
                >
                  <Avatar className="h-6 w-6 shrink-0">
                    <AvatarImage src={authedUrl(e.avatar_url)} />
                    <AvatarFallback className="text-[9px]">
                      {(e.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{e.full_name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{e.designation || e.department || e.email}</div>
                  </div>
                  <Check className={`w-4 h-4 shrink-0 ${value === e.id ? "opacity-100" : "opacity-0"}`} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
